import React from 'react';
import { scheduleEventAvailability } from './eventAvailabilityQueue';
import { useQueries } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import supabaseClient from '../../lib/supabaseClient';
import type { EventListRow } from '../../data/events';
import { revisionedRead } from '../../data/readCache';
import { markRequest } from '../../lib/actionFailureSignals';
import { normalisePlanningReturns, planningReturnWarning } from '../inventory';
import { availabilityTimeErrorMessage, type AvailabilityQueryState } from '../inventory/availabilityErrors';

/**
 * Per-event availability snapshot returned from the batch RPC.
 */
export interface EventAvailability {
  ratio: number;
  totalLines: number;
  availableLines: number;
  shortLines: number;
  returnWarning?: string | null;
  timeError?: string | null;
}

/**
 * Event stages where availability snapshots should NOT be calculated.
 * Once an event reaches shipped or later, items are either deployed or no
 * longer needed, so checking stock overlap is wasted effort.
 */
const AVAILABILITY_SKIP_STAGES = new Set([
  'shipped',
  'on_rent',
  'partially_returned',
  'returning',
  'returned',
  'ready_to_invoice',
  'invoiced',
  'closed',
  'cancelled',
  'void',
  'lost',
]);

// Isolate every event at the RPC boundary. A date-split event can contribute
// thousands of semantic facts; combining it with neighbouring events makes the
// whole call time out, and recursive subdivision can fan out enough concurrent
// work to starve the independent Event Library row query. Production timings
// show that two otherwise-safe singleton calls can contend long enough to cross
// the Data API statement timeout, so advance strictly one event at a time.
const EVENT_AVAILABILITY_CHUNK_SIZE = 1;
const EVENT_AVAILABILITY_CHUNK_CONCURRENCY = 1;

const isStatementTimeoutError = (error: unknown): boolean => {
  const code = typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code?: unknown }).code ?? '')
    : '';
  const message = typeof error === 'object' && error !== null && 'message' in error
    ? String((error as { message?: unknown }).message ?? '')
    : String(error ?? '');
  return code === '57014' || message.toLowerCase().includes('statement timeout');
};

export const shouldRetryEventAvailability = (
  failureCount: number,
  error: unknown,
): boolean => {
  if (isStatementTimeoutError(error) || availabilityTimeErrorMessage(error)) {
    return false;
  }
  return failureCount < 1;
};

/**
 * Returns true when the date string is strictly in the past
 * (before the start of today in local time).
 */
const isDatePast = (
  dateStr: string | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!dateStr) return false;
  const normalized = dateStr.trim();
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
  const ms = dateOnly
    ? new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
    ).getTime()
    : new Date(normalized).getTime();
  if (Number.isNaN(ms)) return false;
  const todayStart = new Date(now.getTime());
  todayStart.setHours(0, 0, 0, 0);
  return ms < todayStart.getTime();
};

/**
 * Availability stays relevant through the inventory return date, which can be
 * later than the show/event end. Keep this shared with EventsPage so the page
 * cannot hide an indicator that the hook would otherwise calculate.
 */
export const isEventAvailabilityRelevant = (
  event: EventListRow,
  now: Date = new Date(),
): boolean => {
  const normalizedStatus = normalizeEventAvailabilityStatus(event.status);
  if (normalizedStatus && AVAILABILITY_SKIP_STAGES.has(normalizedStatus)) {
    return false;
  }
  if (isDatePast(
    event.return_date ?? event.event_end_date ?? event.show_end_date,
    now,
  )) {
    return false;
  }
  return Boolean(event.site_location_id);
};

/** Raised when the RPC is absent so callers can leave every indicator unknown. */
const MISSING_AVAILABILITY_RPC = Symbol('missing-availability-rpc');

/**
 * Run the availability RPC for one set of ids. Returns only the rows the
 * database actually reported — filling in defaults is the caller's job, because
 * only the caller knows which ids were genuinely covered by a successful call.
 */
async function runAvailabilityRatioRpc(
  eventIds: string[],
  supabase: SupabaseClient,
  signal?: AbortSignal,
): Promise<Map<string, EventAvailability>> {
  return revisionedRead(supabase, { eventId: eventIds[0] }, `availability-ratio:${eventIds.join(',')}`, ['inventory', 'lines', 'header'], async () => {
  const rows = new Map<string, EventAvailability>();

  let response = await scheduleEventAvailability(
    () => markRequest(supabase.rpc('batch_event_availability_forecast', { p_event_ids: eventIds }), 'read'),
    signal,
  );
  const missingForecast = response.error && ['42883', 'PGRST202'].includes(response.error.code);
  if (missingForecast) response = await scheduleEventAvailability(
    () => markRequest(supabase.rpc('batch_event_availability_ratio' as any, { p_event_ids: eventIds }), 'read'), signal,
  );
  const { data, error } = response;

  if (error) {
    // If the new RPC doesn't exist yet (migration not applied), signal the
    // caller to stay unknown rather than presenting a false green result.
    if (error.code === '42883' || error.message?.includes('does not exist')) {
      console.warn('[Events availability] batch_event_availability_ratio not found — migration not applied yet');
      throw MISSING_AVAILABILITY_RPC;
    }
    throw error;
  }

  if (Array.isArray(data)) {
    for (const row of data) {
      const eventId = row?.event_id;
      const ratio = Number(row?.ratio);
      const totalLines = Number(row?.total_lines) || 0;
      const availableLines = Number(row?.available_lines) || 0;
      if (typeof eventId === 'string' && eventId && Number.isFinite(ratio)) {
        rows.set(eventId, {
          ratio,
          totalLines,
          availableLines,
          shortLines: totalLines - availableLines,
          returnWarning: missingForecast ? 'Return risk has not been checked. Open the event to check returns.'
            : planningReturnWarning({ planningReturns: normalisePlanningReturns(row.planning_returns) }),
        });
      }
    }
  }

  return rows;
  });
}

type AvailabilityCollection = {
  rows: Map<string, EventAvailability>;
  /** Ids answered by a successful call. Only these may be defaulted. */
  covered: Set<string>;
  /** First failure of any kind, rethrown when nothing at all was answered. */
  failure: unknown;
};

/**
 * Evaluate `eventIds`, isolating accidental multi-event input serially when
 * the database gives up on it.
 *
 * A statement timeout means the batch as a whole was too heavy, not that any
 * particular event is unanswerable. The normal caller supplies exactly one id;
 * serial subdivision is only a defensive fallback. An event that times out on
 * its own stays uncovered, so that indicator reads unknown instead of dragging
 * the Event Library row query down with it.
 */
async function collectAvailability(
  eventIds: string[],
  supabase: SupabaseClient,
  into: AvailabilityCollection,
  signal?: AbortSignal,
): Promise<void> {
  if (eventIds.length === 0) return;

  try {
    const rows = await runAvailabilityRatioRpc(eventIds, supabase, signal);
    for (const [eventId, availability] of rows) {
      into.rows.set(eventId, availability);
    }
    for (const id of eventIds) into.covered.add(id);
    return;
  } catch (error) {
    if (error === MISSING_AVAILABILITY_RPC) {
      // Nothing to subdivide — the function is absent for every id.
      return;
    }

    // Remember the first failure. If no part of this chunk succeeds the caller
    // rethrows it, so React Query retains the last known-good indicators rather
    // than replacing them with unknown.
    if (into.failure === undefined) into.failure = error;

    if (!isStatementTimeoutError(error)) {
      console.error('[Events availability] RPC error:', error);
      return;
    }

    if (eventIds.length === 1) {
      console.warn(
        '[Events availability] event too heavy to evaluate; leaving indicator unknown:',
        eventIds[0],
      );
      return;
    }

    for (const eventId of eventIds) {
      await collectAvailability([eventId], supabase, into, signal);
    }
  }
}

/**
 * Fetch one bounded Event Library availability chunk. The database evaluates
 * dated demand, physical stock, reservations, transfers, and quote overlap,
 * then returns one worst-date verdict per unique event item.
 */
async function fetchEventsAvailabilityChunk(
  eventIds: string[],
  options?: { supabase?: SupabaseClient; signal?: AbortSignal },
): Promise<Map<string, EventAvailability>> {
  const supabase = options?.supabase ?? supabaseClient;
  const result = new Map<string, EventAvailability>();

  if (eventIds.length === 0) return result;

  const collection: AvailabilityCollection = {
    rows: result,
    covered: new Set<string>(),
    failure: undefined,
  };

  await collectAvailability(eventIds, supabase, collection, options?.signal);

  if (collection.covered.size === 0 && collection.failure !== undefined) {
    // Nothing at all resolved. Reject so React Query retains the last
    // successful data during a refetch — resolving an empty snapshot here would
    // replace known-good indicators with unknown after one failure.
    throw collection.failure;
  }

  // Events with no eligible physical or non-physical item rows do not appear
  // in the RPC response and have no inventory shortage to display. Only default
  // ids a successful call actually covered — an unanswered event must stay
  // absent so the UI reads unknown instead of a false "fully available".
  for (const id of collection.covered) {
    if (!result.has(id)) {
      result.set(id, {
        ratio: 1,
        totalLines: 0,
        availableLines: 0,
        shortLines: 0,
      });
    }
  }

  return result;
}

export async function fetchEventsAvailabilityMap(
  eventIds: string[],
  options?: { supabase?: SupabaseClient; signal?: AbortSignal },
): Promise<Map<string, EventAvailability>> {
  const result = new Map<string, EventAvailability>();
  const uniqueEventIds = Array.from(new Set(eventIds.filter(Boolean)));
  let firstFailure: unknown;

  for (let start = 0; start < uniqueEventIds.length; start += EVENT_AVAILABILITY_CHUNK_SIZE) {
    const chunk = uniqueEventIds.slice(start, start + EVENT_AVAILABILITY_CHUNK_SIZE);
    try {
      const chunkResult = await fetchEventsAvailabilityChunk(chunk, options);
      for (const [eventId, availability] of chunkResult) {
        result.set(eventId, availability);
      }
    } catch (error) {
      if (firstFailure === undefined) firstFailure = error;
    }
  }

  if (result.size === 0 && firstFailure !== undefined) {
    throw firstFailure;
  }

  return result;
}

type EventAvailabilityQueryState = [
  id: string,
  status: string | null,
  locationId: string | null,
  fillDate: string | null,
  returnDate: string | null,
  eventStartDate: string | null,
  eventEndDate: string | null,
  showStartDate: string | null,
  showEndDate: string | null,
];

const normalizeEventAvailabilityStatus = (status: string | null | undefined): string | null => {
  const normalized = String(status ?? '').trim().toLowerCase();
  return normalized || null;
};

const eventAvailabilityQueryState = (event: EventListRow): EventAvailabilityQueryState => [
  event.id,
  normalizeEventAvailabilityStatus(event.status),
  event.site_location_id ?? null,
  event.fill_date ?? null,
  event.return_date ?? null,
  event.event_start_date ?? null,
  event.event_end_date ?? null,
  event.show_start_date ?? null,
  event.show_end_date ?? null,
];

/**
 * Event Library availability changes when an event crosses the confirmation
 * boundary, moves location, or changes its requested window. Keep those
 * inputs in the cache key so a same-ID stage transition cannot reuse the
 * pre-confirmation snapshot while the refetch is in flight.
 */
export const eventsAvailabilityQueryKey = (events: EventListRow[]) => [
  'events',
  'availability',
  [...events]
    .map(eventAvailabilityQueryState)
    .sort((a, b) => a[0].localeCompare(b[0])),
];

// An event that times out on its own times out again on the next 2-minute or
// focus refresh, and each attempt holds the serialized queue and a database
// connection for the full statement timeout. Remember the timeout for the
// event's current availability inputs and answer from memory until the
// cool-down passes or its stage, site or dates change.
const EVENT_AVAILABILITY_TIMEOUT_COOLDOWN_MS = 15 * 60 * 1000;
const timedOutAvailability = new Map<string, { retryAt: number; error: unknown }>();

async function fetchEventsAvailabilityChunkWithCooldown(
  chunk: EventListRow[],
  options?: { supabase?: SupabaseClient; signal?: AbortSignal },
): Promise<Map<string, EventAvailability>> {
  const key = JSON.stringify(chunk.map(eventAvailabilityQueryState));
  const remembered = timedOutAvailability.get(key);
  // Rethrowing keeps any earlier indicator, and timeouts are never retried.
  if (remembered && remembered.retryAt > Date.now()) throw remembered.error;
  try {
    const result = await fetchEventsAvailabilityChunk(chunk.map(event => event.id), options);
    timedOutAvailability.delete(key);
    return result;
  } catch (error) {
    if (isStatementTimeoutError(error)) {
      const now = Date.now();
      for (const [staleKey, entry] of timedOutAvailability) {
        if (entry.retryAt <= now) timedOutAvailability.delete(staleKey);
      }
      timedOutAvailability.set(key, { retryAt: now + EVENT_AVAILABILITY_TIMEOUT_COOLDOWN_MS, error });
    }
    throw error;
  }
}

const EMPTY_AVAILABILITY_MAP: Map<string, EventAvailability> = new Map();

export function useEventsAvailabilityMap(
  events: EventListRow[] | undefined,
  options?: { supabase?: SupabaseClient; enabled?: boolean },
): Map<string, EventAvailability> {
  const eligibleEvents = React.useMemo(() => {
    const byId = new Map<string, EventListRow>();
    for (const event of (events ?? [])) {
      if (!isEventAvailabilityRelevant(event)) continue;
      if (event.id && !byId.has(event.id)) {
        byId.set(event.id, event);
      }
    }
    return Array.from(byId.values());
  }, [events]);

  const chunks = React.useMemo(() => {
    const next: EventListRow[][] = [];
    for (let start = 0; start < eligibleEvents.length; start += EVENT_AVAILABILITY_CHUNK_SIZE) {
      next.push(eligibleEvents.slice(start, start + EVENT_AVAILABILITY_CHUNK_SIZE));
    }
    return next;
  }, [eligibleEvents]);
  const chunksKey = React.useMemo(
    () => JSON.stringify(chunks.map(chunk => eventsAvailabilityQueryKey(chunk)[2])),
    [chunks],
  );
  const enabled = (options?.enabled !== false) && chunks.length > 0;
  const [schedule, setSchedule] = React.useState({
    key: '',
    enabledChunkCount: EVENT_AVAILABILITY_CHUNK_CONCURRENCY,
  });

  // Use the new key's first wave synchronously so a prior large result set
  // cannot briefly enable every chunk for a newly selected date/filter range.
  const scheduledChunkCount = schedule.key === chunksKey
    ? Math.min(schedule.enabledChunkCount, chunks.length)
    : Math.min(EVENT_AVAILABILITY_CHUNK_CONCURRENCY, chunks.length);

  React.useEffect(() => {
    setSchedule({
      key: chunksKey,
      enabledChunkCount: Math.min(EVENT_AVAILABILITY_CHUNK_CONCURRENCY, chunks.length),
    });
  }, [chunks.length, chunksKey]);

  const chunkQueries = useQueries({
    queries: chunks.map((chunk, index) => ({
      queryKey: eventsAvailabilityQueryKey(chunk),
      enabled: enabled && index < scheduledChunkCount,
      staleTime: 30 * 1000,
      // Keep open Event Library indicators boundedly fresh when another user
      // confirms/releases an event or transfer. TanStack pauses this interval
      // in background tabs, and focus forces an immediate refresh.
      // Only a source correction/invalidation or explicit retry can resolve an
      // invalid local time. Do not repeat it on focus, remount or a timer.
      refetchOnMount: (query: AvailabilityQueryState) => !availabilityTimeErrorMessage(query.state.error) || query.state.isInvalidated === true,
      refetchOnWindowFocus: (query: AvailabilityQueryState) => availabilityTimeErrorMessage(query.state.error) ? false : 'always' as const,
      refetchInterval: (query: AvailabilityQueryState) => availabilityTimeErrorMessage(query.state.error) ? false : 2 * 60 * 1000,
      refetchIntervalInBackground: false,
      retry: shouldRetryEventAvailability,
      retryDelay: 500,
      queryFn: ({ signal }) => fetchEventsAvailabilityChunkWithCooldown(chunk, {
        supabase: options?.supabase,
        signal,
      }),
    })),
  });

  const settledScheduledChunks = chunkQueries
    .slice(0, scheduledChunkCount)
    .filter(query => query.isSuccess || query.isError)
    .length;

  React.useEffect(() => {
    if (!enabled
      || scheduledChunkCount === 0
      || scheduledChunkCount >= chunks.length
      || settledScheduledChunks < scheduledChunkCount) {
      return;
    }

    setSchedule(current => {
      if (current.key !== chunksKey) {
        return {
          key: chunksKey,
          enabledChunkCount: Math.min(EVENT_AVAILABILITY_CHUNK_CONCURRENCY, chunks.length),
        };
      }
      return {
        key: chunksKey,
        enabledChunkCount: Math.min(
          current.enabledChunkCount + EVENT_AVAILABILITY_CHUNK_CONCURRENCY,
          chunks.length,
        ),
      };
    });
  }, [
    chunks.length,
    chunksKey,
    enabled,
    scheduledChunkCount,
    settledScheduledChunks,
  ]);

  return React.useMemo(() => {
    if (!enabled) return EMPTY_AVAILABILITY_MAP;
    const merged = new Map<string, EventAvailability>();
    for (const [index, query] of chunkQueries.entries()) {
      const timeError = availabilityTimeErrorMessage(query.error);
      if (timeError) {
        // Discard a previous green verdict when current source times are
        // invalid. A negative ratio is the existing unknown contract.
        for (const event of chunks[index]) merged.set(event.id, {
          ratio: -1, totalLines: 0, availableLines: 0, shortLines: 0, timeError,
        });
        continue;
      }
      if (!query.data) continue;
      for (const [eventId, availability] of query.data) {
        merged.set(eventId, availability);
      }
    }
    return merged;
  }, [chunkQueries, chunks, enabled]);
}
